import { c as _c } from "react/compiler-runtime";
import { postOrgCredentialsMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoChevronLeft16, IcoGlobe16, IcoLock16 } from '@onefootprint/icons';
import type { CreateAgentCredentialRequest } from '@onefootprint/request-types/dashboard';
import type { BrowserSessionResource } from '@onefootprint/request-types/percy';
import { Button, LinkButton, PinInput } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import type { BrowserAsk } from '../../utils/chat-segments';
import { hostOf } from '../../utils/host-of';
import PinnedCard, { PinnedBody } from '../pinned-card';
import CredentialOnePasswordForm from './credential-one-password-form';
import CredentialPasswordForm from './credential-password-form';
type CredentialMode = 'password' | 'one_password';
type BrowserAskCardProps = {
  ask: BrowserAsk;
  session: BrowserSessionResource;
  paneOpen: boolean;
  onOpenBrowser: () => void;
  onSendMessage: (content: string) => void;
  onSubmitCode: (code: string) => Promise<void>;
};
const BrowserAskCard = t0 => {
  const $ = _c(74);
  const {
    ask,
    session,
    paneOpen,
    onOpenBrowser,
    onSendMessage,
    onSubmitCode
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const isMobile = useMobileLayout();
  const showRequestErrorToast = useRequestErrorToast();
  const [mode, setMode] = useState("password");
  const [signInInstead, setSignInInstead] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = postOrgCredentialsMutation();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== showRequestErrorToast) {
    t2 = {
      ...t1,
      onError: showRequestErrorToast
    };
    $[1] = showRequestErrorToast;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const saveCredential = useMutation(t2);
  const [codeAttempt, setCodeAttempt] = useState(0);
  let t3;
  if ($[3] !== onSubmitCode) {
    t3 = code => onSubmitCode(code);
    $[3] = onSubmitCode;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setCodeAttempt(_temp);
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = {
      mutationFn: t3,
      onError: t4
    };
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const enterCode = useMutation(t5);
  let T0;
  let handingOff;
  let onePassword;
  let pending;
  let renderBody;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== ask || $[9] !== codeAttempt || $[10] !== enterCode || $[11] !== isMobile || $[12] !== mode || $[13] !== onOpenBrowser || $[14] !== onSendMessage || $[15] !== paneOpen || $[16] !== saveCredential || $[17] !== session || $[18] !== signInInstead || $[19] !== t) {
    const host = hostOf(ask.kind === "credential" ? ask.origin : session.current_url) || t(_temp2);
    handingOff = ask.kind === "handoff" && !signInInstead;
    const canSignInInstead = ask.kind === "handoff" && ask.reason === "login" && session.current_url.startsWith("http");
    let t10;
    if ($[29] !== ask || $[30] !== session) {
      t10 = ask.kind === "credential" ? ask : {
        kind: "credential",
        id: ask.id,
        origin: session.current_url,
        reason: ""
      };
      $[29] = ask;
      $[30] = session;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    const credentialAsk = t10;
    onePassword = mode === "one_password";
    pending = saveCredential.isPending || enterCode.isPending;
    const savedMessage = onePassword ? t(_temp3, {
      host
    }) : t(_temp4, {
      host
    });
    const save = body => {
      if (pending) {
        return;
      }
      saveCredential.mutate({
        body
      }, {
        onSuccess: () => onSendMessage(savedMessage)
      });
    };
    let t11;
    if ($[32] !== enterCode || $[33] !== onSendMessage || $[34] !== pending || $[35] !== t) {
      t11 = code_0 => {
        if (pending) {
          return;
        }
        enterCode.mutate(code_0, {
          onSuccess: () => onSendMessage(t(_temp5))
        });
      };
      $[32] = enterCode;
      $[33] = onSendMessage;
      $[34] = pending;
      $[35] = t;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] !== codeAttempt || $[38] !== enterCode.isError || $[39] !== enterCode.isPending || $[40] !== t11) {
      t12 = {
        pending: enterCode.isPending,
        failed: enterCode.isError,
        attempt: codeAttempt,
        submit: t11
      };
      $[37] = codeAttempt;
      $[38] = enterCode.isError;
      $[39] = enterCode.isPending;
      $[40] = t11;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    const codeEntry = t12;
    renderBody = () => {
      if (handingOff && ask.kind === "handoff") {
        return <HandoffActions ask={ask} host={host} paneOpen={paneOpen} onOpenBrowser={onOpenBrowser} onSendMessage={onSendMessage} codeEntry={codeEntry} />;
      }
      if (onePassword) {
        return <CredentialOnePasswordForm pending={pending} onSave={save} />;
      }
      return <CredentialPasswordForm ask={credentialAsk} pending={pending} onSave={save} onUseOnePassword={() => setMode("one_password")} />;
    };
    T0 = PinnedCard;
    t8 = "gap-3";
    t9 = isMobile;
    t6 = "flex items-center gap-3 max-md:flex-wrap max-md:gap-y-1 min-w-0 mb-1";
    t7 = onePassword || handingOff && canSignInInstead ? <LinkButton iconComponent={IcoChevronLeft16} iconPosition="left" onClick={() => onePassword ? setMode("password") : setSignInInstead(true)} disabled={pending}>{t(_temp6)}</LinkButton> : <><div className="size-9 shrink-0 rounded-full bg-senary/70 flex items-center justify-center">{handingOff ? <IcoGlobe16 className="text-primary" /> : <IcoLock16 className="text-primary" />}</div><span className="text-label-2 text-primary truncate max-md:max-w-[calc(100%-48px)]">{handingOff ? t(_temp7) : t(_temp8, {
          host
        })}</span></>;
    $[8] = ask;
    $[9] = codeAttempt;
    $[10] = enterCode;
    $[11] = isMobile;
    $[12] = mode;
    $[13] = onOpenBrowser;
    $[14] = onSendMessage;
    $[15] = paneOpen;
    $[16] = saveCredential;
    $[17] = session;
    $[18] = signInInstead;
    $[19] = t;
    $[20] = T0;
    $[21] = handingOff;
    $[22] = onePassword;
    $[23] = pending;
    $[24] = renderBody;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    T0 = $[20];
    handingOff = $[21];
    onePassword = $[22];
    pending = $[23];
    renderBody = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  let t10;
  if ($[42] !== ask.kind || $[43] !== handingOff || $[44] !== onSendMessage || $[45] !== onePassword || $[46] !== pending || $[47] !== t) {
    t10 = !handingOff && !onePassword && <><LinkButton disabled={pending} onClick={ask.kind === "handoff" ? () => setSignInInstead(false) : () => onSendMessage(t(_temp9))}>{t(_temp10)}</LinkButton><span aria-hidden={true} className="text-body-3 text-tertiary">·</span></>;
    $[42] = ask.kind;
    $[43] = handingOff;
    $[44] = onSendMessage;
    $[45] = onePassword;
    $[46] = pending;
    $[47] = t;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== onSendMessage || $[50] !== t) {
    t11 = () => onSendMessage(t(_temp11));
    $[49] = onSendMessage;
    $[50] = t;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== t) {
    t12 = t(_temp12);
    $[52] = t;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== pending || $[55] !== t11 || $[56] !== t12) {
    t13 = <LinkButton className="pr-1" disabled={pending} onClick={t11}>{t12}</LinkButton>;
    $[54] = pending;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== t10 || $[59] !== t13) {
    t14 = <div className="flex items-center gap-1 shrink-0 ml-auto">{t10}{t13}</div>;
    $[58] = t10;
    $[59] = t13;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== t14 || $[62] !== t6 || $[63] !== t7) {
    t15 = <div className={t6}>{t7}{t14}</div>;
    $[61] = t14;
    $[62] = t6;
    $[63] = t7;
    $[64] = t15;
  } else {
    t15 = $[64];
  }
  let t16;
  if ($[65] !== isMobile || $[66] !== renderBody) {
    t16 = isMobile ? <PinnedBody itemCount={1} noFade={true} className="flex flex-col -ml-1 pl-1">{renderBody()}</PinnedBody> : renderBody();
    $[65] = isMobile;
    $[66] = renderBody;
    $[67] = t16;
  } else {
    t16 = $[67];
  }
  let t17;
  if ($[68] !== T0 || $[69] !== t15 || $[70] !== t16 || $[71] !== t8 || $[72] !== t9) {
    t17 = <T0 className={t8} shrinkable={t9}>{t15}{t16}</T0>;
    $[68] = T0;
    $[69] = t15;
    $[70] = t16;
    $[71] = t8;
    $[72] = t9;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  return t17;
};
const HANDOFF_STEPS = ['login', 'otp', 'captcha', 'confirm'] as const;
type HandoffStep = (typeof HANDOFF_STEPS)[number] | 'other';
const handoffStep = (reason: string): HandoffStep => HANDOFF_STEPS.find(step => step === reason) ?? 'other';
type CodeEntry = {
  pending: boolean;
  failed: boolean;
  attempt: number;
  submit: (code: string) => void;
};
type HandoffActionsProps = {
  ask: Extract<BrowserAsk, {
    kind: 'handoff';
  }>;
  host: string;
  paneOpen: boolean;
  onOpenBrowser: () => void;
  onSendMessage: (content: string) => void;
  codeEntry: CodeEntry;
};
const HandoffActions = t0 => {
  const $0 = _c(37);
  const {
    ask,
    host,
    paneOpen,
    onOpenBrowser,
    onSendMessage,
    codeEntry
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const [inBrowser, setInBrowser] = useState(false);
  let t1;
  if ($0[0] !== ask.reason) {
    t1 = handoffStep(ask.reason);
    $0[0] = ask.reason;
    $0[1] = t1;
  } else {
    t1 = $0[1];
  }
  const step = t1;
  const attempt = step === "captcha" ? ask.message : "";
  if (step === "otp" && ask.acceptsCode && !inBrowser) {
    let t2;
    if ($0[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => setInBrowser(true);
      $0[2] = t2;
    } else {
      t2 = $0[2];
    }
    let t3;
    if ($0[3] !== codeEntry || $0[4] !== host) {
      t3 = <HandoffCodeEntry host={host} codeEntry={codeEntry} onUseBrowser={t2} />;
      $0[3] = codeEntry;
      $0[4] = host;
      $0[5] = t3;
    } else {
      t3 = $0[5];
    }
    return t3;
  }
  let t2;
  if ($0[6] !== step) {
    t2 = $ => $.chat.browser.ask.step[step];
    $0[6] = step;
    $0[7] = t2;
  } else {
    t2 = $0[7];
  }
  let t3;
  if ($0[8] !== host || $0[9] !== t || $0[10] !== t2) {
    t3 = t(t2, {
      host
    });
    $0[8] = host;
    $0[9] = t;
    $0[10] = t2;
    $0[11] = t3;
  } else {
    t3 = $0[11];
  }
  let t4;
  if ($0[12] !== t3) {
    t4 = <span className="text-body-2 text-secondary">{t3}</span>;
    $0[12] = t3;
    $0[13] = t4;
  } else {
    t4 = $0[13];
  }
  let t5;
  if ($0[14] !== attempt) {
    t5 = attempt && <span className="text-body-3 text-tertiary line-clamp-2">{attempt}</span>;
    $0[14] = attempt;
    $0[15] = t5;
  } else {
    t5 = $0[15];
  }
  let t6;
  if ($0[16] !== t4 || $0[17] !== t5) {
    t6 = <div className="flex flex-col gap-1">{t4}{t5}</div>;
    $0[16] = t4;
    $0[17] = t5;
    $0[18] = t6;
  } else {
    t6 = $0[18];
  }
  let t7;
  if ($0[19] !== onSendMessage || $0[20] !== t) {
    t7 = () => onSendMessage(t(_temp13));
    $0[19] = onSendMessage;
    $0[20] = t;
    $0[21] = t7;
  } else {
    t7 = $0[21];
  }
  let t8;
  if ($0[22] !== t) {
    t8 = t(_temp14);
    $0[22] = t;
    $0[23] = t8;
  } else {
    t8 = $0[23];
  }
  let t9;
  if ($0[24] !== t7 || $0[25] !== t8) {
    t9 = <Button variant="primary" size="compact" onClick={t7}>{t8}</Button>;
    $0[24] = t7;
    $0[25] = t8;
    $0[26] = t9;
  } else {
    t9 = $0[26];
  }
  let t10;
  if ($0[27] !== onOpenBrowser || $0[28] !== paneOpen || $0[29] !== t) {
    t10 = !paneOpen && <LinkButton onClick={onOpenBrowser}>{t(_temp15)}</LinkButton>;
    $0[27] = onOpenBrowser;
    $0[28] = paneOpen;
    $0[29] = t;
    $0[30] = t10;
  } else {
    t10 = $0[30];
  }
  let t11;
  if ($0[31] !== t10 || $0[32] !== t9) {
    t11 = <div className="flex items-center gap-3">{t9}{t10}</div>;
    $0[31] = t10;
    $0[32] = t9;
    $0[33] = t11;
  } else {
    t11 = $0[33];
  }
  let t12;
  if ($0[34] !== t11 || $0[35] !== t6) {
    t12 = <div className="flex flex-col gap-4">{t6}{t11}</div>;
    $0[34] = t11;
    $0[35] = t6;
    $0[36] = t12;
  } else {
    t12 = $0[36];
  }
  return t12;
};
type HandoffCodeEntryProps = {
  host: string;
  codeEntry: CodeEntry;
  onUseBrowser: () => void;
};
const HandoffCodeEntry = t0 => {
  const $ = _c(28);
  const {
    host,
    codeEntry,
    onUseBrowser
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const labelId = useId();
  let t1;
  if ($[0] !== host || $[1] !== t) {
    t1 = t(_temp16, {
      host
    });
    $[0] = host;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== labelId || $[4] !== t1) {
    t2 = <span id={labelId} className="text-body-2 text-secondary">{t1}</span>;
    $[3] = labelId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== codeEntry.failed || $[7] !== t) {
    t3 = codeEntry.failed ? t(_temp17) : undefined;
    $[6] = codeEntry.failed;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== codeEntry.attempt || $[10] !== codeEntry.failed || $[11] !== codeEntry.pending || $[12] !== codeEntry.submit || $[13] !== t3) {
    t4 = <PinInput key={codeEntry.attempt} autoFocus={true} disabled={codeEntry.pending} hasError={codeEntry.failed} hint={t3} onComplete={codeEntry.submit} />;
    $[9] = codeEntry.attempt;
    $[10] = codeEntry.failed;
    $[11] = codeEntry.pending;
    $[12] = codeEntry.submit;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== labelId || $[16] !== t4) {
    t5 = <div role="group" aria-labelledby={labelId}>{t4}</div>;
    $[15] = labelId;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const t6 = codeEntry.pending;
  let t7;
  if ($[18] !== t) {
    t7 = t(_temp18);
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== codeEntry.pending || $[21] !== onUseBrowser || $[22] !== t7) {
    t8 = <LinkButton onClick={onUseBrowser} disabled={t6}>{t7}</LinkButton>;
    $[20] = codeEntry.pending;
    $[21] = onUseBrowser;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t2 || $[25] !== t5 || $[26] !== t8) {
    t9 = <div className="flex flex-col gap-3">{t2}{t5}{t8}</div>;
    $[24] = t2;
    $[25] = t5;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
};
export default BrowserAskCard;
function _temp(count) {
  return count + 1;
}
function _temp2($) {
  return $.chat.resources.browserSession.label;
}
function _temp3($_0) {
  return $_0.chat.browser.credential.onePassword.saved;
}
function _temp4($_1) {
  return $_1.chat.browser.credential.saved;
}
function _temp5($_2) {
  return $_2.chat.browser.ask.code.enteredMessage;
}
function _temp6($_3) {
  return $_3.chat.browser.ask.backToSignIn;
}
function _temp7($_4) {
  return $_4.chat.browser.ask.inControl;
}
function _temp8($_5) {
  return $_5.chat.browser.credential.title;
}
function _temp9($_6) {
  return $_6.chat.browser.credential.manualMessage;
}
function _temp10($_7) {
  return $_7.chat.browser.credential.manual;
}
function _temp11($_8) {
  return $_8.chat.browser.ask.skipMessage;
}
function _temp12($_9) {
  return $_9.chat.browser.ask.skip;
}
function _temp13($_0) {
  return $_0.chat.browser.ask.handBackMessage;
}
function _temp14($_1) {
  return $_1.chat.browser.ask.handBack;
}
function _temp15($_2) {
  return $_2.chat.browser.ask.openBrowser;
}
function _temp16($) {
  return $.chat.browser.ask.code.step;
}
function _temp17($_0) {
  return $_0.chat.browser.ask.code.error;
}
function _temp18($_1) {
  return $_1.chat.browser.ask.code.inBrowser;
}